import React, { useEffect, useState, useCallback } from 'react';
import { Routes, Route, Link, useNavigate, useLocation, Navigate } from 'react-router-dom';
import { supabase, isSupabaseConfigured } from '../services/supabase';
import { getPhotos, getReviews, getSuccessStories } from '../services/apiService';
import { getAllLeads } from '../services/leadService';
import { 
  LayoutDashboard, 
  Image as ImageIcon, 
  Star, 
  GraduationCap, 
  ClipboardList, 
  LogOut, 
  TrendingUp, 
  Activity,
  Building2,
  Settings,
  Menu, // Added
  X     // Added
} from 'lucide-react';

// Import sub-components (Assuming these exist as per your original code)
import AdminPhotos from './AdminPhotos';
import AdminReviews from './AdminReviews';
import AdminSuccessStories from './AdminSuccessStories';
import AdminLeads from './AdminLeads';
import AdminPartneredUniversities from './AdminPartneredUniversities';
import AdminCarousel from './AdminCarousel';
import AdminCarouselSettings from './AdminCarouselSettings';
import AdminPopupSettings from './AdminPopupSettings';
import AdminStudents from './AdminStudents';

const AdminDashboard = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false); // Mobile Menu State
  const [stats, setStats] = useState({
    leads: 0,
    photos: 0,
    reviews: 0,
    successStories: 0,
    recentLeads: [],
  });
  const [statsLoading, setStatsLoading] = useState(true);

  // --- LOAD STATS FUNCTION ---
  const loadStats = useCallback(async () => {
    try {
      setStatsLoading(true);
      const [photos, reviews, stories, leads] = await Promise.all([
        getPhotos('all'),
        getReviews(),
        getSuccessStories(),
        getAllLeads(),
      ]);

      const sortedLeads = [...leads].sort((a, b) => {
          const dateA = new Date(a.created_at || a.timestamp || 0);
          const dateB = new Date(b.created_at || b.timestamp || 0);
          return dateB - dateA;
      });

      setStats({
        leads: leads.length,
        photos: photos.length,
        reviews: reviews.length,
        successStories: stories.length,
        recentLeads: sortedLeads.slice(0, 5),
      });
    } catch (error) {
      console.error('Error loading stats:', error);
    } finally {
      setStatsLoading(false);
    }
  }, []);

  // Auto-refresh stats
  useEffect(() => {
    loadStats();
    const interval = setInterval(() => loadStats(), 30000);
    const handleDataChange = () => loadStats();
    window.addEventListener('dataUpdated', handleDataChange);
    return () => {
      clearInterval(interval);
      window.removeEventListener('dataUpdated', handleDataChange);
    };
  }, [loadStats]);

  // --- AUTHENTICATION ---
  useEffect(() => {
    if (!isSupabaseConfigured) {
      navigate('/admin/login');
      setIsLoading(false);
      return;
    }

    const checkAuth = async () => {
      try {
        const { data: { session }, error } = await supabase.auth.getSession();
        if (error || !session) {
          navigate('/admin/login');
          setIsLoading(false);
          return;
        }
        setIsAuthenticated(true);
      } catch (error) {
        navigate('/admin/login');
      } finally {
        setIsLoading(false);
      }
    };
    checkAuth();

    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      if (session) setIsAuthenticated(true);
      else {
        setIsAuthenticated(false);
        navigate('/admin/login');
      }
    });
    return () => subscription.unsubscribe();
  }, [navigate]);

  useEffect(() => {
    if (isAuthenticated) loadStats();
  }, [isAuthenticated, loadStats]);

  const handleLogout = async () => {
    await supabase.auth.signOut();
    navigate('/admin/login');
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
            <div className="w-10 h-10 border-4 border-slate-200 border-t-red-600 rounded-full animate-spin"></div>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) return null;

  const navItems = [
    { path: '/admin/dashboard', label: 'Dashboard', icon: LayoutDashboard },
    { path: '/admin/leads', label: 'Leads', icon: ClipboardList },
    { path: '/admin/photos', label: 'Gallery', icon: ImageIcon },
    { path: '/admin/reviews', label: 'Reviews', icon: Star },
    { path: '/admin/success-stories', label: 'Success Stories', icon: GraduationCap },
    { path: '/admin/universities', label: 'Universities', icon: Building2 },
    { path: '/admin/students', label: 'Students', icon: GraduationCap },
    { path: '/admin/carousel', label: 'Carousel', icon: ImageIcon },
    { path: '/admin/carousel-settings', label: 'Carousel Settings', icon: Settings },
    { path: '/admin/popup-settings', label: 'Popup Settings', icon: Settings },
  ];

  return (
    <div className="min-h-screen bg-slate-50 flex font-sans overflow-hidden min-h-[111.2vh]">
      
      {/* =========================================
          MOBILE OVERLAY
      ========================================= */}
      {isMobileMenuOpen && (
        <div 
          className="fixed inset-0 bg-slate-900/50 z-20 lg:hidden backdrop-blur-sm transition-opacity"
          onClick={() => setIsMobileMenuOpen(false)}
        />
      )}

      {/* =========================================
          SIDEBAR
      ========================================= */}
      <aside 
        className={`
          fixed inset-y-0 left-0 w-64 bg-slate-900 text-white z-30 
          transform transition-transform duration-300 ease-in-out border-r border-slate-800 flex flex-col
          ${isMobileMenuOpen ? 'translate-x-0' : '-translate-x-full'} 
          lg:translate-x-0 lg:static lg:flex
        `}
      >
        {/* Logo Area */}
        <div className="p-6 border-b border-white/10 flex justify-between items-center">
          <div>
            <img src="/logo.jpg" alt="6th Sense" className="h-8 w-auto object-contain mb-2" />
            <p className="text-xs text-slate-400 uppercase tracking-wider font-bold">Admin Console</p>
          </div>
          {/* Mobile Close Button */}
          <button 
            onClick={() => setIsMobileMenuOpen(false)}
            className="lg:hidden text-slate-400 hover:text-white"
          >
            <X size={24} />
          </button>
        </div>

        {/* Navigation */}
        <nav className="flex-1 py-6 px-3 space-y-1 overflow-y-auto">
          {navItems.map((item) => {
            const isActive = location.pathname === item.path;
            const Icon = item.icon;
            return (
              <Link
                key={item.path}
                to={item.path}
                onClick={() => setIsMobileMenuOpen(false)} // Close menu on click
                className={`flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-medium transition-all duration-200 group ${
                  isActive
                    ? 'bg-red-600 text-white shadow-lg shadow-red-900/20'
                    : 'text-slate-400 hover:bg-white/5 hover:text-white'
                }`}
              >
                <Icon size={18} className={isActive ? 'text-white' : 'text-slate-500 group-hover:text-white'} />
                {item.label}
              </Link>
            );
          })}
        </nav>

        {/* Logout */}
        <div className="p-4 border-t border-white/10">
          <button
            onClick={handleLogout}
            className="flex items-center gap-2 w-full px-4 py-2 text-sm font-medium text-slate-400 hover:text-white hover:bg-white/5 rounded-lg transition-colors"
          >
            <LogOut size={16} />
            Sign Out
          </button>
        </div>
      </aside>

      {/* =========================================
          MAIN CONTENT AREA
      ========================================= */}
      {/* Added h-screen and overflow-y-auto to allow scrolling independent of sidebar */}
      <main className="flex-1 h-[111.2vh] overflow-y-auto relative w-full">
        
        {/* Header */}
        <div className="sticky top-0 z-10 bg-white/80 backdrop-blur-md border-b border-slate-200 px-4 md:px-8 py-4 flex justify-between items-center">
            <div className="flex items-center gap-4">
                {/* Hamburger Button */}
                <button 
                  onClick={() => setIsMobileMenuOpen(true)}
                  className="lg:hidden p-1 -ml-1 text-slate-600 hover:bg-slate-100 rounded-md"
                >
                  <Menu size={24} />
                </button>
                
                <h2 className="text-lg font-bold text-slate-800 capitalize truncate">
                  {location.pathname.split('/').pop().replace('-', ' ')}
                </h2>
            </div>
            
            <div className="flex items-center gap-3">
                <span className="hidden md:inline text-sm text-slate-500">Administrator</span>
                <div className="w-8 h-8 rounded-full bg-red-100 flex items-center justify-center text-red-700 font-bold text-xs">
                    AD
                </div>
            </div>
        </div>

        {/* Page Content - Optimized padding for mobile */}
        <div className="p-4 md:p-8 max-w-7xl mx-auto">
          <Routes>
            <Route
              path="dashboard"
              element={
                <div className="space-y-8">
                  {/* Stats Grid */}
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
                    <StatCard 
                        title="Total Leads" 
                        value={stats.leads} 
                        icon={ClipboardList} 
                        color="blue" 
                        loading={statsLoading} 
                        link="/admin/leads"
                    />
                    <StatCard 
                        title="Photos" 
                        value={stats.photos} 
                        icon={ImageIcon} 
                        color="purple" 
                        loading={statsLoading} 
                        link="/admin/photos"
                    />
                    <StatCard 
                        title="Reviews" 
                        value={stats.reviews} 
                        icon={Star} 
                        color="yellow" 
                        loading={statsLoading} 
                        link="/admin/reviews"
                    />
                    <StatCard 
                        title="Stories" 
                        value={stats.successStories} 
                        icon={GraduationCap} 
                        color="green" 
                        loading={statsLoading} 
                        link="/admin/success-stories"
                    />
                  </div>

                  {/* Recent Leads Table */}
                  <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
                    <div className="px-4 md:px-6 py-5 border-b border-slate-100 flex justify-between items-center">
                        <div className="flex items-center gap-2">
                            <Activity size={20} className="text-slate-400" />
                            <h3 className="font-bold text-slate-800">Recent Activity</h3>
                        </div>
                        <Link to="/admin/leads" className="text-xs font-bold text-red-600 hover:text-red-700 flex items-center gap-1">
                            View All <TrendingUp size={14} />
                        </Link>
                    </div>
                    
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm text-left">
                        <thead className="bg-slate-50 text-slate-500 font-medium border-b border-slate-100">
                          <tr>
                            <th className="px-4 md:px-6 py-3 whitespace-nowrap">Name</th>
                            <th className="px-4 md:px-6 py-3 whitespace-nowrap">Contact</th>
                            <th className="px-4 md:px-6 py-3 whitespace-nowrap">Type</th>
                            <th className="px-4 md:px-6 py-3 whitespace-nowrap">Date</th>
                            <th className="px-4 md:px-6 py-3 whitespace-nowrap">Status</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-50">
                          {statsLoading ? (
                             [1,2,3].map(i => (
                                 <tr key={i} className="animate-pulse">
                                     <td colSpan="5" className="px-6 py-4"><div className="h-4 bg-slate-100 rounded w-full"></div></td>
                                 </tr>
                             ))
                          ) : stats.recentLeads.length > 0 ? (
                            stats.recentLeads.map((lead, index) => (
                              <tr key={lead.id || `lead-${index}`} className="hover:bg-slate-50 transition-colors">
                                <td className="px-4 md:px-6 py-4 font-medium text-slate-900 whitespace-nowrap">{lead.name || 'Unknown'}</td>
                                <td className="px-4 md:px-6 py-4 text-slate-500">
                                    <div className="flex flex-col">
                                        <span className="truncate max-w-[150px]">{lead.email}</span>
                                        <span className="text-xs text-slate-400">{lead.phone}</span>
                                    </div>
                                </td>
                                <td className="px-4 md:px-6 py-4 whitespace-nowrap">
                                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-slate-100 text-slate-800 capitalize">
                                        {lead.type ? lead.type.replace(/_/g, ' ') : 'General'}
                                    </span>
                                </td>
                                <td className="px-4 md:px-6 py-4 text-slate-500 whitespace-nowrap">
                                    {lead.created_at || lead.timestamp
                                      ? new Date(lead.created_at || lead.timestamp).toLocaleDateString()
                                      : '-'}
                                </td>
                                <td className="px-4 md:px-6 py-4 whitespace-nowrap">
                                    <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-50 text-green-700 border border-green-200">
                                        <span className="w-1.5 h-1.5 rounded-full bg-green-500"></span> New
                                    </span>
                                </td>
                              </tr>
                            ))
                          ) : (
                            <tr>
                                <td colSpan="5" className="px-6 py-8 text-center text-slate-400">No recent leads found.</td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              }
            />
            <Route path="photos" element={<AdminPhotos />} />
            <Route path="reviews" element={<AdminReviews />} />
            <Route path="success-stories" element={<AdminSuccessStories />} />
            <Route path="leads" element={<AdminLeads />} />
            <Route path="universities" element={<AdminPartneredUniversities />} />
            <Route path="students" element={<AdminStudents />} />
            <Route path="carousel" element={<AdminCarousel />} />
            <Route path="carousel-settings" element={<AdminCarouselSettings />} />
            <Route path="popup-settings" element={<AdminPopupSettings />} />
            <Route path="" element={<Navigate to="/admin/dashboard" replace />} />
          </Routes>
        </div>
      </main>
    </div>
  );
};

// --- HELPER COMPONENT: STAT CARD ---
const StatCard = ({ title, value, icon: Icon, color, loading, link }) => {
    const colors = {
        blue: 'bg-blue-50 text-blue-600',
        purple: 'bg-purple-50 text-purple-600',
        yellow: 'bg-yellow-50 text-yellow-600',
        green: 'bg-emerald-50 text-emerald-600',
    };

    return (
        <Link to={link} className="block group">
            <div className="bg-white p-6 rounded-2xl border border-slate-200 shadow-sm hover:shadow-md hover:border-red-100 transition-all duration-300">
                <div className="flex justify-between items-start mb-4">
                    <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${colors[color] || 'bg-slate-100 text-slate-600'}`}>
                        <Icon size={24} />
                    </div>
                    <span className="text-xs font-medium text-slate-400 bg-slate-50 px-2 py-1 rounded-full group-hover:bg-red-50 group-hover:text-red-600 transition-colors">
                        Manage &rarr;
                    </span>
                </div>
                <div>
                    <p className="text-slate-500 text-sm font-medium">{title}</p>
                    {loading ? (
                        <div className="h-8 w-20 bg-slate-100 rounded mt-1 animate-pulse"></div>
                    ) : (
                        <h3 className="text-3xl font-bold text-slate-900">{value}</h3>
                    )}
                </div>
            </div>
        </Link>
    );
};

export default AdminDashboard;
